import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Edit2,
  KeyRound,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import ConfirmDialog from '../components/ConfirmDialog';
import SettingsModal from '../components/SettingsModal';
import SettingsPageHeader from '../components/SettingsPageHeader';
import StatusBadge from '../components/StatusBadge';
import ToastMessage from '../components/ToastMessage';
import { useToast } from '../hooks/useToast';
import { rbacSettingsApi } from '../services/rbacSettingsApi';
import { getApiErrorMessage } from '../services/settingsResponse';
import { userSettingsApi } from '../services/userSettingsApi';

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

const emptyUserForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  password: '',
  confirm_password: '',
};

const UserField = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </span>
    {children}
  </label>
);

const UserAccessSettingsPage = () => {
  const { schoolId } = useSchool();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyUserForm);
  const [creating, setCreating] = useState(false);
  const [editState, setEditState] = useState({ open: false, user: null, loading: false });
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone_number: '' });
  const [editing, setEditing] = useState(false);
  const [statusUser, setStatusUser] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [roleState, setRoleState] = useState({ open: false, user: null });
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [removeRole, setRemoveRole] = useState(null);
  const { toast, showToast, clearToast } = useToast();

  const loadUsers = useCallback(
    async (query = '') => {
      if (!schoolId) return;
      setLoading(true);
      try {
        const result = await userSettingsApi.listUsers(schoolId, {
          search: query,
          page: 1,
          pageSize: 100,
        });
        setUsers(result.items);
        setCount(result.count);
      } catch (error) {
        showToast(getApiErrorMessage(error, 'Failed to load users.'), 'error');
      } finally {
        setLoading(false);
      }
    },
    [schoolId, showToast],
  );

  const loadRoles = useCallback(async () => {
    if (!schoolId) return;
    try {
      const list = await rbacSettingsApi.listRoles(schoolId);
      setRoles(list);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to load roles.'), 'error');
    }
  }, [schoolId, showToast]);

  useEffect(() => {
    loadUsers('');
    loadRoles();
  }, [loadUsers, loadRoles]);

  const handleSearch = (event) => {
    event.preventDefault();
    loadUsers(search);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (createForm.password !== createForm.confirm_password) {
      showToast('Password and confirmation do not match.', 'error');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        email: createForm.email,
        password: createForm.password,
        first_name: createForm.first_name,
        last_name: createForm.last_name,
        phone_number: createForm.phone_number,
      };
      await userSettingsApi.createUser(schoolId, payload);
      setCreateOpen(false);
      setCreateForm(emptyUserForm);
      await loadUsers(search);
      showToast('User created successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to create user.'), 'error');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = async (user) => {
    setEditState({ open: true, user, loading: true });
    try {
      const detail = await userSettingsApi.getUser(schoolId, user.id);
      setEditState({ open: true, user: detail, loading: false });
      setEditForm({
        first_name: detail.first_name || '',
        last_name: detail.last_name || '',
        phone_number: detail.phone_number || '',
      });
    } catch (error) {
      setEditState({ open: false, user: null, loading: false });
      showToast(getApiErrorMessage(error, 'Failed to load user details.'), 'error');
    }
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    if (!editState.user) return;
    setEditing(true);
    try {
      await userSettingsApi.updateUser(schoolId, editState.user.id, editForm);
      setEditState({ open: false, user: null, loading: false });
      await loadUsers(search);
      showToast('User details updated successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update user.'), 'error');
    } finally {
      setEditing(false);
    }
  };

  const handleStatus = async () => {
    if (!statusUser) return;
    setStatusSaving(true);
    try {
      await userSettingsApi.updateUserStatus(
        schoolId,
        statusUser.id,
        !statusUser.is_active,
      );
      setStatusUser(null);
      await loadUsers(search);
      showToast(`User ${statusUser.is_active ? 'deactivated' : 'activated'} successfully.`);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update user status.'), 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const loadAssignedRoles = async (userId) => {
    setRolesLoading(true);
    try {
      const list = await rbacSettingsApi.listUserRoles(schoolId, userId);
      setAssignedRoles(list);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to load assigned roles.'), 'error');
    } finally {
      setRolesLoading(false);
    }
  };

  const openRoles = async (user) => {
    setRoleState({ open: true, user });
    setSelectedRoleId('');
    await loadAssignedRoles(user.id);
  };

  const availableRoles = useMemo(() => {
    const assignedIds = new Set(assignedRoles.map((role) => role.id));
    return roles.filter((role) => !assignedIds.has(role.id));
  }, [roles, assignedRoles]);

  const handleAssignRole = async () => {
    if (!roleState.user || !selectedRoleId) return;
    setRoleSaving(true);
    try {
      await rbacSettingsApi.assignUserRole(
        schoolId,
        roleState.user.id,
        selectedRoleId,
      );
      setSelectedRoleId('');
      await loadAssignedRoles(roleState.user.id);
      showToast('Role assigned successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to assign role.'), 'error');
    } finally {
      setRoleSaving(false);
    }
  };

  const handleRemoveRole = async () => {
    if (!roleState.user || !removeRole) return;
    setRoleSaving(true);
    try {
      await rbacSettingsApi.removeUserRole(
        schoolId,
        roleState.user.id,
        removeRole.id,
      );
      setRemoveRole(null);
      await loadAssignedRoles(roleState.user.id);
      showToast('Role removed successfully.');
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to remove role.'), 'error');
    } finally {
      setRoleSaving(false);
    }
  };

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <SettingsPageHeader
        title="Users & Access"
        description="Create login accounts, update users, activate or deactivate access, and manage role assignments."
        action={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Add user
          </button>
        }
      />

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex w-full max-w-md gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name or email..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <button
              type="submit"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Search
            </button>
          </form>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{count} users</span>
            <button
              type="button"
              onClick={() => loadUsers(search)}
              disabled={loading}
              className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center text-sm text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading users...
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] text-left">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-semibold">User</th>
                  <th className="px-4 py-3 font-semibold">Verified</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-700">
                          {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {user.full_name || 'Unnamed user'}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.is_verified ? 'Verified' : 'Not verified'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge active={user.is_active} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openRoles(user)}
                          className="rounded-lg p-2 text-gray-500 hover:bg-violet-50 hover:text-violet-700"
                          title="Manage roles"
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(user)}
                          className="rounded-lg p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-700"
                          title="Edit user"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setStatusUser(user)}
                          className={`rounded-lg p-2 text-gray-500 ${
                            user.is_active
                              ? 'hover:bg-red-50 hover:text-red-600'
                              : 'hover:bg-green-50 hover:text-green-600'
                          }`}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SettingsModal
        open={createOpen}
        title="Create user"
        description="The School Admin enters the initial password manually."
        onClose={() => !creating && setCreateOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-user-form"
              disabled={creating}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create user
            </button>
          </div>
        }
      >
        <form id="create-user-form" onSubmit={handleCreate} className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <UserField label="First name" required>
            <input
              required
              value={createForm.first_name}
              onChange={(event) =>
                setCreateForm((current) => ({ ...current, first_name: event.target.value }))
              }
              className={inputClass}
            />
          </UserField>
          <UserField label="Last name">
            <input
              value={createForm.last_name}
              onChange={(event) =>
                setCreateForm((current) => ({ ...current, last_name: event.target.value }))
              }
              className={inputClass}
            />
          </UserField>
          <div className="md:col-span-2">
            <UserField label="Email" required>
              <input
                required
                type="email"
                value={createForm.email}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, email: event.target.value }))
                }
                className={inputClass}
              />
            </UserField>
          </div>
          <div className="md:col-span-2">
            <UserField label="Phone number">
              <input
                value={createForm.phone_number}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, phone_number: event.target.value }))
                }
                className={inputClass}
              />
            </UserField>
          </div>
          <UserField label="Initial password" required>
            <input
              required
              type="password"
              minLength="8"
              value={createForm.password}
              onChange={(event) =>
                setCreateForm((current) => ({ ...current, password: event.target.value }))
              }
              className={inputClass}
            />
          </UserField>
          <UserField label="Confirm password" required>
            <input
              required
              type="password"
              minLength="8"
              value={createForm.confirm_password}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  confirm_password: event.target.value,
                }))
              }
              className={inputClass}
            />
          </UserField>
        </form>
      </SettingsModal>

      <SettingsModal
        open={editState.open}
        title="Edit user"
        description={editState.user?.email || 'Loading user details...'}
        onClose={() => !editing && setEditState({ open: false, user: null, loading: false })}
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditState({ open: false, user: null, loading: false })}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-user-form"
              disabled={editing || editState.loading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {editing && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </button>
          </div>
        }
      >
        {editState.loading ? (
          <div className="flex min-h-[180px] items-center justify-center text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading...
          </div>
        ) : (
          <form id="edit-user-form" onSubmit={handleEdit} className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <UserField label="First name" required>
              <input
                required
                value={editForm.first_name}
                onChange={(event) =>
                  setEditForm((current) => ({ ...current, first_name: event.target.value }))
                }
                className={inputClass}
              />
            </UserField>
            <UserField label="Last name">
              <input
                value={editForm.last_name}
                onChange={(event) =>
                  setEditForm((current) => ({ ...current, last_name: event.target.value }))
                }
                className={inputClass}
              />
            </UserField>
            <div className="md:col-span-2">
              <UserField label="Phone number">
                <input
                  value={editForm.phone_number}
                  onChange={(event) =>
                    setEditForm((current) => ({ ...current, phone_number: event.target.value }))
                  }
                  className={inputClass}
                />
              </UserField>
            </div>
          </form>
        )}
      </SettingsModal>

      <SettingsModal
        open={roleState.open}
        title="Manage user roles"
        description={roleState.user ? `${roleState.user.full_name || roleState.user.email} • ${roleState.user.email}` : ''}
        onClose={() => !roleSaving && setRoleState({ open: false, user: null })}
        maxWidth="max-w-xl"
      >
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Assign another role</h3>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <select
                value={selectedRoleId}
                onChange={(event) => setSelectedRoleId(event.target.value)}
                className={`${inputClass} mt-0 flex-1`}
              >
                <option value="">Select role</option>
                {availableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAssignRole}
                disabled={!selectedRoleId || roleSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {roleSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Assign
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Assigned roles</h3>
            {rolesLoading ? (
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading roles...
              </div>
            ) : assignedRoles.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No roles are assigned to this user.
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {assignedRoles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center justify-between rounded-xl border border-gray-200 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-violet-50 p-2 text-violet-700">
                        <UserCog className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{role.name}</p>
                        <p className="text-xs text-gray-500">{role.slug}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRemoveRole(role)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="Remove role"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SettingsModal>

      <ConfirmDialog
        open={Boolean(statusUser)}
        title={statusUser?.is_active ? 'Deactivate user' : 'Activate user'}
        message={
          statusUser?.is_active
            ? 'This disables login access but keeps the user and related records.'
            : 'This restores login access for the user.'
        }
        confirmLabel={statusUser?.is_active ? 'Deactivate' : 'Activate'}
        danger={Boolean(statusUser?.is_active)}
        loading={statusSaving}
        onCancel={() => setStatusUser(null)}
        onConfirm={handleStatus}
      />

      <ConfirmDialog
        open={Boolean(removeRole)}
        title="Remove role"
        message={`Remove the ${removeRole?.name || ''} role from this user? The user account will not be deleted.`}
        confirmLabel="Remove role"
        danger
        loading={roleSaving}
        onCancel={() => setRemoveRole(null)}
        onConfirm={handleRemoveRole}
      />
    </>
  );
};

export default UserAccessSettingsPage;
